import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { IonButton, PopoverController } from '@ionic/angular/standalone';
import { AdminRowMenuComponent } from './admin-row-menu.component';
import { RowAction, splitRowActions } from './row-actions';

/**
 * A table row's actions: the main one as a small pill, everything else in a
 * "⋯" menu (a popover, so the table's scroll container can't clip it), with
 * destructive actions last and in red. Replaces rows of 4–8 buttons.
 */
@Component({
  selector: 'admin-row-actions',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonButton, RouterLink],
  template: `
    @if (split.primary; as p) {
      @if (p.link) {
        <ion-button size="small" fill="outline" [routerLink]="p.link" [queryParams]="p.queryParams">{{
          p.label
        }}</ion-button>
      } @else {
        <ion-button size="small" fill="outline" [color]="p.danger ? 'danger' : undefined" (click)="p.run?.()">{{
          p.label
        }}</ion-button>
      }
    }
    @if (split.menu.length) {
      <button
        type="button"
        class="more"
        [attr.aria-label]="'More actions' + (label ? ' for ' + label : '')"
        (click)="open($event)"
      >
        <span class="material-symbols-outlined" aria-hidden="true">more_horiz</span>
      </button>
    }
  `,
  styles: [
    `
      :host {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        white-space: nowrap;
      }
      ion-button {
        margin: 0;
      }
      .more {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border: 1px solid var(--cnt-line-strong, rgba(34, 34, 34, 0.15));
        border-radius: 9999px;
        background: #fff;
        color: var(--cnt-ink, #222);
        cursor: pointer;
      }
      .more:hover {
        border-color: var(--cnt-trinidad, #e3530d);
        color: var(--cnt-trinidad, #e3530d);
      }
      .more:focus-visible {
        outline: 2px solid var(--cnt-green, #295d42);
        outline-offset: 2px;
      }
    `,
  ],
})
export class AdminRowActionsComponent {
  private readonly popovers = inject(PopoverController);
  private readonly router = inject(Router);

  split: ReturnType<typeof splitRowActions> = { menu: [] };

  @Input({ required: true }) set actions(actions: RowAction[]) {
    this.split = splitRowActions(actions);
  }

  /** Names the row for screen readers: "More actions for Pine Camp". */
  @Input() label = '';

  async open(event: Event): Promise<void> {
    const popover = await this.popovers.create({
      component: AdminRowMenuComponent,
      componentProps: { actions: this.split.menu },
      event,
      side: 'bottom',
      alignment: 'end',
      cssClass: 'admin-row-popover',
    });
    await popover.present();
    const { data } = await popover.onDidDismiss<RowAction>();
    if (!data) return;
    if (data.link) void this.router.navigate(data.link, { queryParams: data.queryParams });
    else data.run?.();
  }
}
